import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from "@/components/ui/command";
import { ChevronsUpDown, Check } from "lucide-react";
import { byLastName } from "@/lib/format";

const NONE = "__none__";

// Searchable type-ahead employee picker (Popover + cmdk). Filters by name OR email, ordered
// by last name. Optionally renders a "none" entry (noneLabel) that emits `noneValue`.
export const EmployeePicker = ({
  value, onChange, users, placeholder, searchPlaceholder, emptyText,
  noneValue, noneLabel, testid, className = "", disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const sorted = useMemo(() => [...users].sort(byLastName), [users]);
  const selected = value && value !== noneValue ? users.find((u) => u.id === value) : null;
  const isNone = noneLabel !== undefined && (value === noneValue || !value);
  const pick = (v) => { onChange(v); setOpen(false); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" aria-expanded={open} disabled={disabled} data-testid={testid}
          className={`justify-between rounded-sm border-zinc-300 font-normal disabled:bg-zinc-50 disabled:text-zinc-400 ${className}`}>
          <span className="truncate text-left">
            {selected ? selected.displayName : <span className="text-zinc-400">{isNone ? noneLabel : placeholder}</span>}
          </span>
          <ChevronsUpDown size={15} className="ml-2 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] rounded-sm p-0" align="start">
        <Command filter={(val, search) => {
          if (val === NONE) return 1;
          const u = users.find((x) => x.id === val);
          const hay = `${u?.displayName || ""} ${u?.userPrincipalName || ""}`.toLowerCase();
          return hay.includes(search.toLowerCase()) ? 1 : 0;
        }}>
          <CommandInput placeholder={searchPlaceholder} data-testid={`${testid}-search`} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            {noneLabel !== undefined && (
              <CommandItem value={NONE} onSelect={() => pick(noneValue)}>
                <Check size={15} className={`mr-2 shrink-0 ${isNone ? "opacity-100" : "opacity-0"}`} />
                {noneLabel}
              </CommandItem>
            )}
            {sorted.map((u) => (
              <CommandItem key={u.id} value={u.id} data-testid={`${testid}-opt-${u.id}`} onSelect={() => pick(u.id)}>
                <Check size={15} className={`mr-2 shrink-0 ${value === u.id ? "opacity-100" : "opacity-0"}`} />
                <span className="truncate">{u.displayName}</span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
